import type { ProtocolMarket } from "./types";

/**
 * Prescriptions that name two medications given at one hour.
 *
 * A dual trigger is not a procedure. It is two of them, and the app already
 * describes both: leuprolide has a walk and so does hCG. What it needs is a
 * name to be recognised by and a way to be taken apart, because a patient can
 * log the item "Dual Trigger" itself — and that record does not say *which*
 * two products she was given.
 *
 * So the pairing is asked, exactly the way urinary hCG's route is asked: the
 * record is ambiguous, the answer is on her prescription, and nothing infers
 * it. The combinations below are the ones the approved content lists per
 * market, and each names its two halves in the order they are given — the
 * agonist first, as the content's own steps do.
 *
 * Why a table rather than a third protocol: a combined protocol would be an
 * object nothing else in the app has. Her calendar logs medications one at a
 * time, the walk records doses one at a time, and a procedure that was secretly
 * two would have to lie to both (Anaïs, 2026-09-23).
 */
export type ProtocolPairing = {
  /** What her record may call the pair. Matched like any other name. */
  names: readonly string[];
  /** The combinations she picks from, each naming two medications in order. */
  choices: readonly {
    /** Exactly as the content lists it, and as her box will read. */
    label: string;
    markets: readonly ProtocolMarket[];
    /** Two names, each resolving to a walk on its own. */
    walks: readonly [string, string];
  }[];
};

export const DUAL_TRIGGER_PAIRING: ProtocolPairing = {
  /**
   * Every way a record can name this, including the combinations themselves.
   *
   * The first draft listed only "Dual Trigger", and the combination names were
   * therefore *not* recognised as pairs: "Lupron + Pregnyl" contains "Pregnyl",
   * so it resolved to urinary hCG and walked her through one of her two
   * injections, with the agonist never mentioned. Those five strings are the
   * app's own — `brandsByCountry` lists them per market — so they are the
   * likeliest thing on a row, and they are here.
   *
   * The French generic name is here for the same reason it was missing:
   * nothing in the app produces "Double déclenchement", and the content's own
   * French is "Leuproréline + hCG faible dose (déclencheur dual)".
   */
  names: [
    "Dual Trigger",
    "déclencheur dual",
    "Leuproréline + hCG",
    "Leuprolide + low-dose hCG",
    ...[
      "Lupron + Pregnyl",
      "Lupron + Ovidrel",
      "Lucrin + Ovitrelle",
      "Prostap + Pregnyl",
      "Lucrin + Ovidrel",
    ],
  ],
  choices: [
    {
      label: "Lupron + Pregnyl",
      markets: ["US"],
      walks: ["Lupron", "Pregnyl"],
    },
    {
      label: "Lupron + Ovidrel",
      markets: ["US"],
      walks: ["Lupron", "Ovidrel"],
    },
    {
      label: "Lucrin + Ovitrelle",
      markets: ["GB", "EU"],
      walks: ["Lucrin", "Ovitrelle"],
    },
    {
      label: "Prostap + Pregnyl",
      markets: ["GB"],
      walks: ["Prostap", "Pregnyl"],
    },
    {
      label: "Lucrin + Ovidrel",
      markets: ["AU"],
      walks: ["Lucrin", "Ovidrel"],
    },
  ],
};

export const ALL_PROTOCOL_PAIRINGS: readonly ProtocolPairing[] = [
  DUAL_TRIGGER_PAIRING,
];

/**
 * The combination her record already names, if it names one.
 *
 * "Dual Trigger" says a pair and not which; "Lupron + Pregnyl" says both. A
 * question whose answer is written on the row is a question worth not asking,
 * and one more tap between her and an injection that is already timed.
 */
export function choiceFromPrescribedName(
  pairing: ProtocolPairing,
  prescribedName: string,
): ProtocolPairing["choices"][number] | null {
  const normalized = fold(prescribedName);
  if (!normalized) return null;
  return (
    pairing.choices.find((choice) => normalized.includes(fold(choice.label))) ??
    null
  );
}

function fold(text: string): string {
  return text
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

/**
 * The pairing her record names, if it names one.
 *
 * Substring and accent-insensitive, like every other name in this package, and
 * deliberately checked *before* the protocols: "Dual Trigger" is not any
 * medication's trade name, so nothing else would answer to it.
 */
export function pairingFromPrescribedName(
  prescribedName: string,
): ProtocolPairing | null {
  const normalized = fold(prescribedName);
  if (!normalized) return null;

  return (
    ALL_PROTOCOL_PAIRINGS.find((pairing) =>
      pairing.names.some((name) => normalized.includes(fold(name))),
    ) ?? null
  );
}
